import React, { memo } from 'react'
import { Row, useContainerWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { MediaPlay } from '@fs/zion-icon'
import { useConferenceContext, useTimeTravel, isContentAvailable } from '@fs/zion-rootstech'
import CardListItem from '../CardListItem'
import Timestamp from '../../Timestamp'
import { LiveBadge, SeriesBadge, DraftBadge, InPersonBadge, SessionDifficultyBadge, OnlineBadge } from '../../Badge'
import {
  getIsLive,
  isSeriesConferencePromo,
  isDraft,
  getRelativePath,
  getCustomImage,
  isSessionConferencePromo,
  getDuration,
} from '../../helpers'
import { PillButton, PlaylistsButton, FollowSeriesButton, SocialShareButton } from '../../buttons'
import { getIsPlaylistable } from '../../../hooks'

const Badges = ({ isLive, isSeries, isDraftSession, promo, ...props }) => {
  const location = promo?.sessionLocation
  return (
    <Row gutters="nano" {...props} wrap>
      {isLive && <LiveBadge />}
      {isSeries && <SeriesBadge />}
      {isDraftSession && <DraftBadge />}
      {location?.includes('In Person') && <InPersonBadge />}
      {location?.includes('Online') && <OnlineBadge />}
      {promo?.sessionDifficulty && <SessionDifficultyBadge sessionDifficulty={promo?.sessionDifficulty} />}
    </Row>
  )
}

const Actions = ({ to, data, linkPage, isSeries, asIconButton, isInPerson }) => {
  const [t] = useTranslation()
  const { getNow } = useTimeTravel()
  const { getConferenceYear } = useConferenceContext()
  const { title, contentId, url, description, id, promo, contentType } = data
  const thumbnail = getCustomImage(data)
  const isPlaylistable = isInPerson
    ? false
    : getIsPlaylistable({
        contentType: promo?.contentType || contentType,
      })

  const isAvailable = isContentAvailable({ item: data, now: getNow(), currentConferenceYear: getConferenceYear() })
  const ariaLabel = t('series.watch.action.label', 'Watch: { videoTitle }', { videoTitle: title })
  return (
    <Row alignX={isInPerson ? 'end' : 'start'}>
      {isAvailable ? (
        <PillButton
          growX
          emphasis="medium"
          to={to}
          Icon={MediaPlay}
          data-testid="list-item-watch"
          linkName={`Watch: ${title}: Button`}
          aria-label={ariaLabel}
        >
          {t('series.watch.action', 'Watch')}
        </PillButton>
      ) : (
        <div growX />
      )}

      <Row gutters="md">
        {isPlaylistable && !isSeries && (
          <PlaylistsButton sessionId={contentId || id} asIconButton={asIconButton} emphasis="low" item={data} />
        )}
        {isPlaylistable && isSeries && <FollowSeriesButton seriesId={contentId || id} />}
        <SocialShareButton
          title={title}
          linkPage={linkPage}
          asIconButton={asIconButton}
          url={url}
          thumbnail={thumbnail}
          description={description}
          size={asIconButton ? 'md' : 'lg'}
        />
      </Row>
    </Row>
  )
}

function SessionCard({ data, headingLevel, linkPage }) {
  const atWidth = useContainerWidth()

  const { conferenceYear, date, title, description, creators, exhibitors, url, promo = {} } = data
  const videoDuration = getDuration(data)
  const isInPerson = promo?.sessionLocation === 'In Person'

  const isLive = getIsLive({ date, videoDuration })
  const isSeries = isSeriesConferencePromo(promo)
  const isDraftSession = isDraft(data)
  const to = getRelativePath(url)
  const asIconButton = atWidth({ default: true, xl: false })
  const isSession = isSessionConferencePromo(promo)
  const linkName = `Watch: ${title}: Title`
  const videoData = isInPerson ? null : { ...data, isSession, thumbnailLinkName: `Watch: ${title}: Thumbnail` }

  return (
    <CardListItem
      badges={<Badges isLive={isLive} isSeries={isSeries} promo={promo} isDraftSession={isDraftSession} />}
      timestamp={<Timestamp conferenceYear={conferenceYear} timestamp={date} />}
      heading={title}
      linkName={linkName}
      description={description}
      creators={creators}
      exhibitors={exhibitors}
      to={to}
      dataForVideo={videoData}
      headingLevel={headingLevel}
      actions={
        <Actions
          to={to}
          data={data}
          linkPage={linkPage}
          isSeries={isSeries}
          asIconButton={asIconButton}
          isInPerson={isInPerson}
        />
      }
    />
  )
}

export default memo(SessionCard)
